import {
    Mesh, MeshLambertMaterial, MeshStandardMaterial, MeshPhysicalMaterial,
    LinearMipmapLinearFilter, Object3D, BufferGeometry, Material, Group, Texture
} from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

function enableMipmaps(mat: Material): void {
    const props = ['map', 'aoMap', 'emissiveMap', 'lightMap'] as const;
    for (const key of props) {
        const tex = (mat as unknown as Record<string, Texture | undefined>)[key];
        if (tex) {
            tex.generateMipmaps = true;
            tex.minFilter = LinearMipmapLinearFilter;
        }
    }
}

function geoAttrKey(geo: BufferGeometry): string {
    const attrs = Object.keys(geo.attributes).sort().join(',');
    const morph = geo.morphAttributes ? Object.keys(geo.morphAttributes).sort().join(',') : '';
    const morphRel = geo.morphTargetsRelative ? '1' : '0';
    const indexed = geo.index ? '1' : '0';
    return `${attrs}|${morph}|${morphRel}|${indexed}`;
}

function bucketKey(mat: MeshLambertMaterial, geo: BufferGeometry): string {
    const c = mat.color;
    const e = mat.emissive;
    return `${c.r.toFixed(4)}_${c.g.toFixed(4)}_${c.b.toFixed(4)}_` +
        `${mat.opacity}_${mat.side}_${mat.transparent}_` +
        `${e.r.toFixed(4)}_${e.g.toFixed(4)}_${e.b.toFixed(4)}_` +
        `${mat.map?.uuid ?? 'null'}_${mat.emissiveMap?.uuid ?? 'null'}_` +
        geoAttrKey(geo);
}

function convertMaterial(mat: Material, geo: BufferGeometry, maps: string[]): Material {
    if (!(mat instanceof MeshStandardMaterial) && !(mat instanceof MeshPhysicalMaterial)) {
        return mat;
    }

    if (mat.aoMap) maps.push('aoMap');
    if (mat.normalMap) maps.push('normalMap');
    if (mat.emissiveMap) maps.push('emissiveMap');
    if (mat.lightMap) maps.push('lightMap');

    const lambert = new MeshLambertMaterial({
        color: mat.color,
        map: mat.map,
        aoMap: mat.aoMap,
        aoMapIntensity: mat.aoMapIntensity,
        emissive: mat.emissive,
        emissiveMap: mat.emissiveMap,
        emissiveIntensity: mat.emissiveIntensity,
        lightMap: mat.lightMap,
        lightMapIntensity: mat.lightMapIntensity,
        transparent: mat.transparent,
        opacity: mat.opacity,
        side: mat.side,
        alphaMap: mat.alphaMap,
        alphaTest: mat.alphaTest,
    });

    enableMipmaps(lambert);

    if (lambert.aoMap || lambert.lightMap) {
        if (geo.attributes.uv && !geo.attributes.uv2) {
            geo.setAttribute('uv2', geo.attributes.uv);
        }
    }

    mat.dispose();
    return lambert;
}

export function optimizeGltfScene(root: Object3D): void {
    let meshCount = 0;
    let triCount = 0;
    const matTypes = new Map<string, number>();
    const maps: string[] = [];

    const buckets = new Map<string, { material: MeshLambertMaterial; geometries: BufferGeometry[] }>();
    const meshesToRemove: Mesh[] = [];

    root.traverse((child) => {
        if (!(child instanceof Mesh)) return;

        meshCount++;
        const geo = child.geometry as BufferGeometry;
        if (geo.index) {
            triCount += geo.index.count / 3;
        } else if (geo.attributes.position) {
            triCount += geo.attributes.position.count / 3;
        }

        child.castShadow = false;
        child.receiveShadow = false;

        const type = child.material.constructor.name;
        matTypes.set(type, (matTypes.get(type) || 0) + 1);

        const converted = convertMaterial(child.material, geo, maps);

        if (converted instanceof MeshLambertMaterial) {
            const cloned = geo.clone();
            child.updateWorldMatrix(true, false);
            cloned.applyMatrix4(child.matrixWorld);

            const key = bucketKey(converted, cloned);

            if (!buckets.has(key)) {
                buckets.set(key, { material: converted, geometries: [cloned] });
            } else {
                buckets.get(key)!.geometries.push(cloned);
                converted.dispose();
            }
            meshesToRemove.push(child);
        } else {
            child.material = converted;
        }
    });

    for (const mesh of meshesToRemove) {
        mesh.geometry.dispose();
        if (mesh.parent) mesh.parent.remove(mesh);
    }

    const merged = new Group();
    merged.name = '__merged';
    let mergedMeshCount = 0;

    for (const [, bucket] of buckets) {
        let mergedGeo: BufferGeometry | null;

        if (bucket.geometries.length === 1) {
            mergedGeo = bucket.geometries[0];
        } else {
            mergedGeo = mergeGeometries(bucket.geometries, false);
            if (!mergedGeo) {
                console.warn(`[GLB] merge failed for bucket (${bucket.geometries.length} geos), falling back to individual meshes`);
                for (const g of bucket.geometries) {
                    const mesh = new Mesh(g, bucket.material.clone());
                    mesh.frustumCulled = true;
                    merged.add(mesh);
                    mergedMeshCount++;
                }
                continue;
            }
            bucket.geometries.forEach(g => g.dispose());
        }

        const mesh = new Mesh(mergedGeo, bucket.material);
        mesh.frustumCulled = true;
        merged.add(mesh);
        mergedMeshCount++;
    }

    root.add(merged);

    const uniqueBuckets = buckets.size;
    console.log(`[GLB optimize]`);
    console.log(`  BEFORE: ${meshCount} meshes | ${triCount | 0} triangles`);
    console.log(`  Materials: ${[...matTypes.entries()].map(([k, v]) => `${v}x ${k}`).join(', ')}`);
    console.log(`  Texture maps found: ${maps.length ? [...new Set(maps)].join(', ') : 'none'}`);
    console.log(`  Unique material+attr combos: ${uniqueBuckets}`);
    console.log(`  AFTER:  ${mergedMeshCount} draw calls (meshes)`);
    console.log(`  Reduction: ${meshCount} → ${mergedMeshCount} (${((1 - mergedMeshCount / meshCount) * 100) | 0}% fewer draw calls)`);
}
